import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { PLANTS } from "../../data/plants";
import { JOURNAL } from "../../data/journal";
import ProductCard from "../../components/fancyplants/ProductCard";
import TrustStrip from "../../components/fancyplants/TrustStrip";
import PlantImage from "../../components/fancyplants/PlantImage";
import Seo from "../../components/fancyplants/Seo";
import { formatDate } from "../../utils/format";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0 } },
};
const rise = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};

export default function HomePage() {
  const featured = PLANTS.filter((p) => p.stock > 0).slice(0, 4);

  return (
    <div>
      <Seo path="/" />
      {/* Hero — the single orchestrated load */}
      <section className="relative min-h-[88vh] overflow-hidden bg-forest-900">
        <div className="absolute inset-0">
          <PlantImage
            src="https://images.unsplash.com/photo-1525498128493-380d1990a112?auto=format&fit=crop&w=1600&q=70"
            alt="A sunlit collection of exotic foliage plants"
            ratio="auto"
            className="h-full w-full"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-green-900 via-green-900/55 to-green-900/35" />
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="container-fp relative flex min-h-[88vh] flex-col justify-end pb-20 pt-32"
        >
          <motion.p variants={rise} className="eyebrow mb-4 flex items-center gap-2 text-brass">
            <Sparkles size={14} /> Exotic plants · grown for the Irish climate
          </motion.p>
          <motion.h1 variants={rise} className="t-display max-w-3xl text-canvas">
            The unusual, the architectural, the quietly{" "}
            <span className="italic text-brass">fancy</span>.
          </motion.h1>
          <motion.p variants={rise} className="mt-5 max-w-xl text-lg text-canvas/80">
            A small, characterful plant shop for people who want something other than the
            big-box shelf. Chosen to thrive in our soft, damp midlands air — and packed to
            arrive healthy.
          </motion.p>
          <motion.div variants={rise} className="mt-8 flex flex-wrap gap-3">
            <Link to="/shop" className="btn-brass">
              Browse the shop <ArrowRight size={16} />
            </Link>
            <Link to="/care" className="btn-outline border-canvas/30 text-canvas hover:border-canvas hover:text-canvas">
              Read the Care Library
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <TrustStrip />

      {/* Featured */}
      <section className="container-fp py-16 md:py-24">
        <div className="mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow mb-2">In stock now</p>
            <h2 className="t-h2 max-w-lg">A few of this season's favourites</h2>
          </div>
          <Link to="/shop" className="hidden shrink-0 items-center gap-1 text-sm font-semibold text-forest-700 hover:gap-2 md:flex">
            See all plants <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
          {featured.map((p, i) => (
            <ProductCard key={p.id} plant={p} index={i} />
          ))}
        </div>
      </section>

      {/* Editorial split — the personal touch */}
      <section className="bg-canvas-raised">
        <div className="container-fp grid items-center gap-10 py-16 md:grid-cols-2 md:py-24">
          <div className="relative">
            <PlantImage
              src="https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1000&q=70"
              alt="A grower tending exotic plants in a glasshouse"
              ratio="4/5"
              className="rounded-3xl"
            />
            <span className="absolute -bottom-5 -right-3 hidden font-display text-[8rem] leading-none text-brass/15 md:block">
              01
            </span>
          </div>
          <div>
            <p className="eyebrow mb-3">The personal touch</p>
            <h2 className="t-h2 mb-5">One grower, real advice, plants that actually suit your home.</h2>
            <p className="t-lead mb-4">
              No warehouse, no call centre. Every plant here was chosen by one person who can
              tell you exactly how it'll cope with a north-facing room in Kilcormac or a
              steamy bathroom in Athlone.
            </p>
            <p className="t-lead mb-6">
              Ask us anything. That conversation is the whole point of a small shop.
            </p>
            <Link to="/about" className="btn-forest">
              Our story <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Journal teaser */}
      <section className="container-fp py-16 md:py-24">
        <div className="mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow mb-2">From the journal</p>
            <h2 className="t-h2">Reading for the green-fingered</h2>
          </div>
          <Link to="/journal" className="hidden shrink-0 items-center gap-1 text-sm font-semibold text-forest-700 hover:gap-2 md:flex">
            All posts <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {JOURNAL.slice(0, 3).map((post) => (
            <Link key={post.id} to={`/journal/${post.slug}`} className="group surface overflow-hidden hover:shadow-lift">
              <PlantImage src={post.cover} alt={post.title} ratio="3/2" />
              <div className="p-5">
                <p className="text-xs text-ink-soft">
                  {formatDate(post.date)} · {post.readingTime}
                </p>
                <h3 className="mt-1 font-display text-xl leading-tight group-hover:text-forest-700">
                  {post.title}
                </h3>
                <p className="mt-2 text-sm text-ink-soft">{post.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
